import {
  Alert,
  AlertDescription,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
} from '@1trading/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { UserCog } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { justificationSchema } from '@/components/dialogs/confirm-action';
import { JustificationField } from '@/components/dialogs/justification-field';
import { WarningAlerts } from '@/components/ui/warning-alerts';
import { USER_CONSOLE_QUERY_KEY } from '@/hooks/queries/users/use-user';
import { getErrorMessage } from '@/lib/api/error';
import {
  isInstitutionalSegment,
  isRestrictedState,
  parseSegmentPartialFailure,
  SEGMENT_ERROR_COPY,
  type SegmentPartialFailure,
  USER_SEGMENTS,
  type UserSegment,
  type UserState,
  updateSegment,
} from '@/services/api/user-console';

const segmentFormSchema = z.object({
  segment: z.enum(USER_SEGMENTS),
  applyFeatureRestrictions: z.boolean(),
  // Shared audit-justification rules (5–1000 chars), same bounds as confirmAction
  justification: justificationSchema,
});

type SegmentFormSchema = z.infer<typeof segmentFormSchema>;

interface SegmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pid: string;
  currentSegment: UserSegment;
  userState: UserState;
}

/**
 * Port of BopUserSegmentDialog. Built as a bespoke Dialog rather than
 * confirmAction because the content is interactive — it reuses the shared
 * justification bounds/copy so the UX matches the other audited mutations.
 */
export const SegmentDialog = ({ open, onOpenChange, pid, currentSegment, userState }: SegmentDialogProps) => {
  const queryClient = useQueryClient();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [partialFailure, setPartialFailure] = useState<SegmentPartialFailure | null>(null);

  const isRestricted = isRestrictedState(userState);

  const form = useForm<SegmentFormSchema>({
    resolver: zodResolver(segmentFormSchema),
    mode: 'onChange',
    defaultValues: { segment: currentSegment, applyFeatureRestrictions: !isRestricted, justification: '' },
  });
  const { isSubmitting, isValid } = form.formState;

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset only when the dialog opens (legacy watcher parity)
  useEffect(() => {
    if (!open) return;
    form.reset({ segment: currentSegment, applyFeatureRestrictions: !isRestricted, justification: '' });
    setSubmitError(null);
    setPartialFailure(null);
  }, [open]);

  // PRESERVE: the API rejects the restrictions decision for a non-active user —
  // force the checkbox off even if the state changes while the dialog is open
  useEffect(() => {
    if (isRestricted) form.setValue('applyFeatureRestrictions', false);
  }, [isRestricted, form]);

  const selectedSegment = form.watch('segment');
  const applyFeatureRestrictions = form.watch('applyFeatureRestrictions');
  const justification = form.watch('justification');

  const hasChange = selectedSegment !== currentSegment;
  const promoting = !isInstitutionalSegment(currentSegment) && isInstitutionalSegment(selectedSegment);
  const demoting = isInstitutionalSegment(currentSegment) && !isInstitutionalSegment(selectedSegment);
  const crossesBoundary = promoting || demoting;

  const warnings: string[] = [];
  if (hasChange) {
    warnings.push(
      'Payment methods are re-evaluated for the new segment — a deposit or withdrawal method may become unavailable.',
      'Regulatory transaction reporting classification changes.',
    );
    if (selectedSegment === 'MARKET_MAKER') warnings.push('USDC becomes eligible as collateral.');
    if (promoting && applyFeatureRestrictions) {
      warnings.push('Futures trading is blocked, including closing an open position — the user cannot exit a futures position until the restriction is cleared.');
    }
    if (demoting) warnings.push('Card payments and debtor name-match verification become active again.');
  }

  const handleConfirm = form.handleSubmit(async (values) => {
    setSubmitError(null);
    setPartialFailure(null);
    try {
      await updateSegment(pid, {
        comment: values.justification.trim(),
        segment: values.segment,
        // PRESERVE: send the decision flag ONLY when crossing the retail
        // boundary — omit the key entirely otherwise (INSTITUTIONAL↔MARKET_MAKER must not send it)
        ...(crossesBoundary ? { applySegmentFeatureRestrictions: values.applyFeatureRestrictions } : {}),
      });
      toast.success(`Segment changed to ${values.segment}`);
      onOpenChange(false);
      // Header re-renders with the new segment; a PENDING statusChange also updates state.
      // Invalidating the pid-scoped base key refreshes every dependent tab query too.
      await queryClient.invalidateQueries({ queryKey: [...USER_CONSOLE_QUERY_KEY, pid] });
    } catch (error) {
      // Partial failure: keep the dialog open — blind writes make re-confirm safe
      const partial = await parseSegmentPartialFailure(error);
      if (partial) {
        setPartialFailure(partial);
        return;
      }
      const message = getErrorMessage(error);
      setSubmitError(SEGMENT_ERROR_COPY[message] ?? message);
    }
  });

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && isSubmitting) return;
    onOpenChange(nextOpen);
  };

  return (
    <Dialog onOpenChange={handleOpenChange} open={open}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCog className="size-5" />
            Change User Segment
          </DialogTitle>
        </DialogHeader>
        <form className="flex flex-col gap-4" noValidate onSubmit={handleConfirm}>
          <Controller
            control={form.control}
            name="segment"
            render={({ field }) => (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="segment-dialog-segment">Segment</Label>
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger id="segment-dialog-segment">
                    <SelectValue placeholder="Select segment" />
                  </SelectTrigger>
                  <SelectContent>
                    {USER_SEGMENTS.map((segment) => (
                      <SelectItem key={segment} value={segment}>
                        {segment}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          />

          <WarningAlerts warnings={warnings} />

          {crossesBoundary ? (
            <div className="flex flex-col gap-1.5">
              <Controller
                control={form.control}
                name="applyFeatureRestrictions"
                render={({ field }) => (
                  <label className="flex items-center gap-2 text-sm font-medium" htmlFor="segment-dialog-apply-restrictions">
                    <Checkbox
                      checked={field.value}
                      disabled={isRestricted}
                      id="segment-dialog-apply-restrictions"
                      onCheckedChange={(checked) => field.onChange(checked === true)}
                    />
                    {promoting ? 'Apply institutional requirements' : 'Remove institutional requirements'}
                  </label>
                )}
              />
              <p className="text-xs text-muted-foreground">
                {promoting
                  ? 'Restricts futures and marks the user pending until LEI registration and the offline questionnaire are complete.'
                  : 'Lifts the futures restriction and clears the LEI and offline-questionnaire pending items.'}
              </p>
              {isRestricted ? <p className="text-xs text-amber-600 dark:text-amber-500">Unavailable while the user is {userState}.</p> : null}
            </div>
          ) : null}

          <JustificationField id="segment-dialog-justification" justification={justification} {...form.register('justification')} />

          {partialFailure ? (
            <Alert variant="destructive">
              <AlertDescription>
                <div>Applied: {partialFailure.applied.join(', ') || 'nothing'}.</div>
                <div>
                  Failed: {partialFailure.failed.target} ({partialFailure.failed.error}).
                </div>
                <div>Re-confirm to finish the change.</div>
              </AlertDescription>
            </Alert>
          ) : submitError ? (
            <Alert variant="destructive">
              <AlertDescription>{submitError}</AlertDescription>
            </Alert>
          ) : null}

          <DialogFooter>
            <Button disabled={isSubmitting} onClick={() => onOpenChange(false)} type="button" variant="outline">
              Cancel
            </Button>
            <Button className="gap-2" disabled={!hasChange || !isValid || isSubmitting} type="submit" variant="primary">
              {isSubmitting ? <Spinner className="size-4" /> : null}
              Change Segment
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
SegmentDialog.displayName = 'SegmentDialog';
